/**
 * SuccessWare API Explorer
 * 
 * Comprehensive test and documentation page for all SuccessWare GraphQL mutations
 */

import React, { useState, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronDown, ChevronUp, Book, Search, X } from 'lucide-react';
import successwareSchema from './outbound_push.json';

interface FieldComment {
  [mutationName: string]: {
    [fieldPath: string]: string;
  };
}

interface TestValues {
  [mutationName: string]: {
    [fieldPath: string]: any;
  };
}

interface MutationInfo {
  name: string;
  description: string | null;
  args: any[];
  type: any;
}

interface InputTypeInfo {
  name: string;
  description: string | null;
  inputFields: any[];
}

export default function SuccessWareApiExplorerPage() {
  const { theme } = useTheme();
  const [expandedMutations, setExpandedMutations] = useState<Set<string>>(new Set());
  const [expandedInputTypes, setExpandedInputTypes] = useState<Set<string>>(new Set());
  const [comments, setComments] = useState<FieldComment>(() => {
    const saved = localStorage.getItem('successware-field-comments');
    return saved ? JSON.parse(saved) : {};
  });
  const [testValues, setTestValues] = useState<TestValues>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'mutations' | 'inputTypes'>('mutations');

  const mutations: MutationInfo[] = successwareSchema.availableMutations;
  const inputTypes: InputTypeInfo[] = successwareSchema.inputTypes;

  // Filter mutations based on search
  const filteredMutations = useMemo(() => {
    if (!searchQuery) return mutations;
    const query = searchQuery.toLowerCase();
    return mutations.filter(m => 
      m.name.toLowerCase().includes(query) ||
      (m.description && m.description.toLowerCase().includes(query))
    );
  }, [searchQuery, mutations]);

  // Filter input types based on search
  const filteredInputTypes = useMemo(() => {
    if (!searchQuery) return inputTypes;
    const query = searchQuery.toLowerCase();
    return inputTypes.filter(t => 
      t.name.toLowerCase().includes(query) ||
      t.inputFields.some(f => f.name.toLowerCase().includes(query))
    );
  }, [searchQuery, inputTypes]);

  const toggleMutation = (name: string) => {
    setExpandedMutations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  const toggleInputType = (name: string) => {
    setExpandedInputTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  const saveComment = (mutationName: string, fieldPath: string, comment: string) => {
    const newComments = {
      ...comments,
      [mutationName]: {
        ...(comments[mutationName] || {}),
        [fieldPath]: comment
      }
    };
    setComments(newComments);
    localStorage.setItem('successware-field-comments', JSON.stringify(newComments));
  };

  const saveTestValue = (mutationName: string, fieldPath: string, value: any) => {
    setTestValues(prev => ({
      ...prev,
      [mutationName]: {
        ...(prev[mutationName] || {}),
        [fieldPath]: value
      }
    }));
  };

  const expandAll = () => {
    if (activeTab === 'mutations') {
      setExpandedMutations(new Set(filteredMutations.map(m => m.name)));
    } else {
      setExpandedInputTypes(new Set(filteredInputTypes.map(t => t.name)));
    }
  };

  const collapseAll = () => {
    if (activeTab === 'mutations') {
      setExpandedMutations(new Set());
    } else {
      setExpandedInputTypes(new Set());
    }
  };

  const getTypeString = (type: any): string => {
    if (!type) return 'Unknown';
    if (type.kind === 'NON_NULL') {
      return `${getTypeString(type.ofType)}!`;
    }
    if (type.kind === 'LIST') {
      return `[${getTypeString(type.ofType)}]`;
    }
    return type.name || 'Unknown';
  };

  const renderField = (field: any, mutationName: string, parentPath: string = '') => {
    const fieldPath = parentPath ? `${parentPath}.${field.name}` : field.name;
    const comment = comments[mutationName]?.[fieldPath] || '';
    const testValue = testValues[mutationName]?.[fieldPath] || '';
    const typeString = getTypeString(field.type);
    const isRequired = typeString.endsWith('!');

    return (
      <div key={fieldPath} className="border-l-2 pl-4 py-2" style={{ borderColor: theme.colors.borderColor }}>
        <div className="grid grid-cols-12 gap-2 items-start">
          {/* Field Name & Type */}
          <div className="col-span-3">
            <div className="font-mono text-sm" style={{ color: theme.colors.textPrimary }}>
              {field.name}
              {isRequired && <span className="text-red-400 ml-1">*</span>}
            </div>
            <div className="text-xs mt-1 font-mono" style={{ color: theme.colors.textSecondary }}>
              {typeString}
            </div>
            {field.defaultValue && (
              <div className="text-xs mt-1" style={{ color: theme.colors.textSecondary }}>
                Default: {field.defaultValue}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="col-span-3">
            {field.description && (
              <div className="text-xs" style={{ color: theme.colors.textSecondary }}>
                {field.description}
              </div>
            )}
          </div>

          {/* Test Value Input */}
          <div className="col-span-3">
            <input
              type="text"
              value={testValue}
              onChange={(e) => saveTestValue(mutationName, fieldPath, e.target.value)}
              placeholder="Test value..."
              className="w-full px-2 py-1 text-xs rounded"
              style={{
                backgroundColor: theme.colors.bgPage,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
              }}
            />
          </div>

          {/* Comment Input */}
          <div className="col-span-3">
            <textarea
              value={comment}
              onChange={(e) => saveComment(mutationName, fieldPath, e.target.value)}
              placeholder="Add documentation note..."
              className="w-full px-2 py-1 text-xs rounded resize-none"
              rows={2}
              style={{
                backgroundColor: theme.colors.bgPage,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderMutation = (mutation: MutationInfo) => {
    const isExpanded = expandedMutations.has(mutation.name);
    const inputArg = mutation.args.find(arg => arg.name === 'input');
    const inputTypeName = inputArg ? getTypeString(inputArg.type).replace('!', '') : null;
    const inputType = inputTypes.find(t => t.name === inputTypeName);

    return (
      <div
        key={mutation.name}
        className="mb-4 rounded-lg overflow-hidden"
        style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}
      >
        {/* Mutation Header */}
        <div
          className="p-4 cursor-pointer flex items-center justify-between hover:opacity-80"
          onClick={() => toggleMutation(mutation.name)}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold font-mono" style={{ color: theme.colors.textPrimary }}>
                {mutation.name}
              </h3>
              <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: theme.colors.bgPage, color: theme.colors.textSecondary }}>
                {mutation.type.name}
              </span>
            </div>
            {mutation.description && (
              <p className="text-sm mt-1" style={{ color: theme.colors.textSecondary }}>
                {mutation.description}
              </p>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" style={{ color: theme.colors.textSecondary }} />
          ) : (
            <ChevronDown className="w-5 h-5" style={{ color: theme.colors.textSecondary }} />
          )}
        </div>

        {/* Mutation Details */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t" style={{ borderColor: theme.colors.borderColor }}>
            {/* Column Headers */}
            <div className="grid grid-cols-12 gap-2 py-3 border-b font-semibold text-xs" style={{ borderColor: theme.colors.borderColor, color: theme.colors.textSecondary }}>
              <div className="col-span-3">Field Name & Type</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-3">Test Value</div>
              <div className="col-span-3">Documentation Note</div>
            </div>

            {/* Input Fields */}
            {inputType ? (
              <div className="mt-2">
                {inputType.inputFields.map(field => renderField(field, mutation.name))}
              </div>
            ) : (
              <div className="py-4 text-sm" style={{ color: theme.colors.textSecondary }}>
                No input type defined for this mutation
              </div>
            )}

            {/* Arguments (non-input) */}
            {mutation.args.filter(arg => arg.name !== 'input').length > 0 && (
              <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.colors.borderColor }}>
                <h4 className="text-sm font-semibold mb-2" style={{ color: theme.colors.textPrimary }}>
                  Additional Arguments
                </h4>
                {mutation.args.filter(arg => arg.name !== 'input').map(arg => 
                  renderField(arg, mutation.name)
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderInputType = (inputType: InputTypeInfo) => {
    const isExpanded = expandedInputTypes.has(inputType.name);

    return (
      <div
        key={inputType.name}
        className="mb-4 rounded-lg overflow-hidden"
        style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}
      >
        {/* Input Type Header */}
        <div
          className="p-4 cursor-pointer flex items-center justify-between hover:opacity-80"
          onClick={() => toggleInputType(inputType.name)}
        >
          <div className="flex-1">
            <h3 className="text-lg font-semibold font-mono" style={{ color: theme.colors.textPrimary }}>
              {inputType.name}
            </h3>
            {inputType.description && (
              <p className="text-sm mt-1" style={{ color: theme.colors.textSecondary }}>
                {inputType.description}
              </p>
            )}
            <div className="text-xs mt-1" style={{ color: theme.colors.textSecondary }}>
              {inputType.inputFields.length} fields
            </div>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" style={{ color: theme.colors.textSecondary }} />
          ) : (
            <ChevronDown className="w-5 h-5" style={{ color: theme.colors.textSecondary }} />
          )}
        </div>

        {/* Input Type Details */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t" style={{ borderColor: theme.colors.borderColor }}>
            {/* Column Headers */}
            <div className="grid grid-cols-12 gap-2 py-3 border-b font-semibold text-xs" style={{ borderColor: theme.colors.borderColor, color: theme.colors.textSecondary }}>
              <div className="col-span-3">Field Name & Type</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-3">Test Value</div>
              <div className="col-span-3">Documentation Note</div>
            </div>

            {/* Fields */}
            <div className="mt-2">
              {inputType.inputFields.map(field => renderField(field, inputType.name))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: theme.colors.bgPage }}>
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm mb-6" style={{ color: theme.colors.textSecondary }}>
        <Link to="/" className="hover:text-white">Home</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/integrations" className="hover:text-white">Integrations</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/integrations/successware" className="hover:text-white">SuccessWare</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-white">API Explorer</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.textPrimary }}>
          SuccessWare API Explorer
        </h1>
        <p style={{ color: theme.colors.textSecondary }}>
          Explore and document all {mutations.length} GraphQL mutations and {inputTypes.length} input types from the SuccessWare API
        </p>
      </div>

      {/* API Info */}
      <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
        <div className="flex items-start gap-3">
          <Book className="w-5 h-5 mt-1" style={{ color: theme.colors.textSecondary }} />
          <div>
            <div className="text-sm font-semibold mb-2" style={{ color: theme.colors.textPrimary }}>
              API Information
            </div>
            <div className="text-xs space-y-1" style={{ color: theme.colors.textSecondary }}>
              <div><strong>Endpoint:</strong> {successwareSchema.graphqlEndpoint}</div>
              <div><strong>Auth Endpoint:</strong> {successwareSchema.authentication.endpoint}</div>
              <div><strong>Auth Method:</strong> {successwareSchema.authentication.method} with username/password → access_token</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Controls */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: theme.colors.textSecondary }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search mutations and fields..."
            className="w-full pl-10 pr-10 py-2 rounded-lg"
            style={{
              backgroundColor: theme.colors.bgCard,
              color: theme.colors.textPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <X className="w-4 h-4" style={{ color: theme.colors.textSecondary }} />
            </button>
          )}
        </div>

        <button
          onClick={expandAll}
          className="px-4 py-2 rounded-lg text-sm font-medium"
          style={{
            backgroundColor: theme.colors.bgCard,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        >
          Expand All
        </button>

        <button
          onClick={collapseAll}
          className="px-4 py-2 rounded-lg text-sm font-medium"
          style={{
            backgroundColor: theme.colors.bgCard,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        >
          Collapse All
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setActiveTab('mutations')}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'mutations' ? '#10b981' : theme.colors.bgCard,
            color: activeTab === 'mutations' ? 'white' : theme.colors.textPrimary,
            border: `1px solid ${activeTab === 'mutations' ? '#10b981' : theme.colors.borderColor}`,
          }}
        >
          Mutations ({filteredMutations.length})
        </button>

        <button
          onClick={() => setActiveTab('inputTypes')}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'inputTypes' ? '#10b981' : theme.colors.bgCard,
            color: activeTab === 'inputTypes' ? 'white' : theme.colors.textPrimary,
            border: `1px solid ${activeTab === 'inputTypes' ? '#10b981' : theme.colors.borderColor}`,
          }}
        >
          Input Types ({filteredInputTypes.length})
        </button>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'mutations' ? (
          <div>
            {filteredMutations.length === 0 ? (
              <div className="text-center py-12" style={{ color: theme.colors.textSecondary }}>
                No mutations found matching "{searchQuery}"
              </div>
            ) : (
              filteredMutations.map(mutation => renderMutation(mutation))
            )}
          </div>
        ) : (
          <div>
            {filteredInputTypes.length === 0 ? (
              <div className="text-center py-12" style={{ color: theme.colors.textSecondary }}>
                No input types found matching "{searchQuery}"
              </div>
            ) : (
              filteredInputTypes.map(inputType => renderInputType(inputType))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
